import { SHCheckbox } from "@components/design-systems";
import { useToggle } from "react-use";
import { useMSTableColumnSettingVisibility } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/column-setting/utils";
import React, { useEffect, useMemo } from "react";
import { debounce } from "lodash";
import { MSColumnSettingsDefaultValueConstants } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/column-setting/constants";

type MSTableColumnSettingSubHeaderVisibilityCheckboxProps = {
  mainHeaderId: string;
  subHeaderId: string;
  defaultChecked?: boolean;
  disabled?: boolean;
};

export const MSTableColumnSettingSubHeaderVisibilityCheckbox = React.memo(
  ({
    mainHeaderId,
    subHeaderId,
    defaultChecked = false,
    disabled = false,
  }: MSTableColumnSettingSubHeaderVisibilityCheckboxProps) => {
    // Forms
    const {
      setValue,
      isSubHeaderVisible: formDataIsVisible,
      currentSubHeaderFieldPath,
    } = useMSTableColumnSettingVisibility({
      mainHeaderId,
      subHeaderId,
    });

    // States
    const [isChecked, toggleChecked] = useToggle(defaultChecked);

    // Functions
    const handleOnClick = () => {
      if (!currentSubHeaderFieldPath) return;
      setTimeout(() => {
        setValue(currentSubHeaderFieldPath.isVisible, !isChecked);
        toggleChecked();
        debounceUpdateValueByFormData.cancel();
      }, MSColumnSettingsDefaultValueConstants.ZeroDelayTimeMs);
    };

    // Effects
    const debounceUpdateValueByFormData = useMemo(
      () =>
        debounce((formDataValue: boolean, currentChecked: boolean) => {
          if (currentChecked === formDataValue) return;
          toggleChecked(formDataValue);
        }, MSColumnSettingsDefaultValueConstants.StandardDelayTimeMs),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    useEffect(() => {
      debounceUpdateValueByFormData(formDataIsVisible, isChecked);

      return () => {
        debounceUpdateValueByFormData.cancel();
      };
    }, [formDataIsVisible, isChecked, debounceUpdateValueByFormData]);

    return (
      <SHCheckbox
        disabled={disabled}
        checked={isChecked}
        onClick={handleOnClick}
        sx={{
          padding: 0.5,
        }}
      />
    );
  },
);
